import useIsProductInCustomAssortment from "./useIsProductInCustomAssortment";
import toast from "react-hot-toast";
import useCurrentUser from "../../auth/hooks/useCurrentUser";
import { useCallback } from "react";
import { useTranslations } from "next-intl";

export enum Roles {
  ADMINISTRATOR = "ADMINISTRATOR",
  ORDERER = "ORDERER",
  ORDERER_RESTRICTED = "ORDERER_RESTRICTED",
  ORDERER_RESTRICTED_STRICT = "ORDERER_RESTRICTED_STRICT",
  USER = "USER",
}

const usePermissions = () => {
  const { isProductInCustomAssortment } = useIsProductInCustomAssortment();
  const { currentUser, loading } = useCurrentUser();
  const t = useTranslations();
  const user = currentUser?.self || {};

  const canOrderClientsProducts = useCallback(
    async (clientId: string, productSku: string): Promise<boolean> => {
      if (
        clientId === user?._id ||
        user?.roles?.includes(Roles.ADMINISTRATOR) ||
        user?.roles?.includes(Roles.ORDERER)
      )
        return true;

      const isInAssortment = await isProductInCustomAssortment({
        productSku,
        clientId: user?._id,
      });

      if (
        user?.roles?.some((role) =>
          [Roles.ORDERER_RESTRICTED, Roles.ADMINISTRATOR].includes(role),
        )
      ) {
        if (!isInAssortment) {
          toast(t("product_not_in_custom_assortment_warning"));
        }
        return true;
      }

      if (user?.roles?.includes(Roles.ORDERER_RESTRICTED_STRICT)) {
        if (!isInAssortment) {
          toast.error(t("can_not_order_for_client_error"));
          return false;
        }
      }

      return true;
    },
    [currentUser],
  );

  return {
    canManageClients: !!user?.roles?.includes(Roles.ADMINISTRATOR),
    canManageEmployees: !!user?.roles?.includes(Roles.ADMINISTRATOR),
    canOrderClientsProducts,
    loading,
  };
};

export default usePermissions;
